import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, Video, CreditCard, BarChart2, Settings, LogOut, X, MessageSquare, Package } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const navGroups = [
  {
    label: 'Overview',
    items: [
      { label: 'Dashboard', to: '/admin', icon: LayoutDashboard },
    ]
  },
  {
    label: 'Management',
    items: [
      { label: 'Users', to: '/admin/users', icon: Users },
      { label: 'Courses', to: '/admin/videos', icon: Video },
      { label: 'Subscriptions', to: '/admin/subscriptions', icon: Package },
    ]
  },
  {
    label: 'Enquiries',
    items: [
      { label: 'Investment Queries', to: '/admin/investment-queries', icon: MessageSquare },
    ]
  },
  {
    label: 'Finance',
    items: [
      { label: 'Payments', to: '/admin/payments', icon: CreditCard },
      { label: 'Reports', to: '/admin/reports', icon: BarChart2 },
    ]
  }
];

interface SidebarProps { open: boolean; onClose: () => void; }

export default function AdminSidebar({ open, onClose }: SidebarProps) {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (to: string) =>
    to === '/admin' ? location.pathname === '/admin' : location.pathname.startsWith(to);

  const handleLogout = async () => {
    await logout();
    toast('Signed out successfully.', 'info');
    navigate('/login');
  };

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-40 bg-black/40 lg:hidden backdrop-blur-sm" onClick={onClose} />
      )}
      <aside className={`sidebar ${open ? 'open' : ''}`}>
        {/* Brand */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-[var(--glass-border)]">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-15 h-15 rounded-lg overflow-hidden flex items-center justify-center bg-[var(--glass-bg)] border border-[var(--glass-border)] shrink-0">
              <img src="/stone-bench-logo.jpeg" alt="SB" className="w-full h-full object-contain"
                onError={e => { e.currentTarget.style.display = 'none'; }} />
            </div>
            <div className="leading-none">
              <p className="text-[14px] font-black tracking-tight text-white pb-1">STONE BENCH</p>
              <p className="text-[9px] font-bold uppercase tracking-widest text-white">INVESTMENTS</p>
            </div>
          </Link>
          <button onClick={onClose} className="lg:hidden p-2 rounded-lg text-white hover:bg-white/10 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="px-3 py-3 flex-1 space-y-4 overflow-y-auto h-[70vh]">
          {navGroups.map(group => (
            <div key={group.label}>
              <p className="text-[9px] font-black uppercase tracking-[0.15em] text-white px-2.5 mb-1.5">{group.label}</p>
              {group.items.map(item => {
                const Icon = item.icon;
                return (
                  <Link key={item.to} to={item.to} onClick={onClose}
                    className={`nav-item ${isActive(item.to) ? 'active' : ''}`}>
                    <Icon className="icon" />{item.label}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* User footer */}
        <div className="px-3 py-3 border-t border-[var(--glass-border)]">
          <div className="flex items-center gap-2.5 px-2 mb-2">
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ background: 'var(--color-accent)' }}>
              {user?.email?.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-bold text-white truncate">{user?.name || 'Admin'}</p>
              <p className="text-[9px] text-white truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout}
            className="nav-item w-full hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20">
            <LogOut className="icon" /> Sign Out
          </button>
        </div>
      </aside>
    </>
  );
}
